/* Signing in to the admin portal.
 *
 * The same screen as the dashboard's, for the same two reasons: the providers
 * the auth service reports and no others, because a button for a provider
 * nobody registered sends somebody to an error page on another domain; and
 * email by code, because nothing that takes a password ships.
 *
 * IT DOES NOT ASK WHETHER YOU ARE STAFF. Signing in is signing in; whether this
 * account may read anything is the API's answer, and the shell shows it. A
 * sign-in screen that refused early would tell a stranger which addresses are
 * on the list.
 */
import { Offline, RateLimited, type OAuthProvider, type Session } from "@damga/kit";
import { useState } from "react";

import { t } from "../content";

type Auth = {
  requestEmailCode(email: string): Promise<void>;
  verifyEmailCode(email: string, code: string): Promise<Session>;
};

const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

export function SignIn({
  auth,
  providers,
  error,
  onProvider,
  onSignedIn,
}: {
  auth: Auth;
  providers: Set<OAuthProvider> | null;
  error: string | null;
  onProvider: (p: OAuthProvider) => void;
  onSignedIn: (s: Session) => void;
}) {
  const [step, setStep] = useState<"choose" | "email" | "code">("choose");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function send() {
    if (!LOOKS_LIKE_EMAIL.test(email.trim())) {
      setProblem(t("auth.badEmail"));
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await auth.requestEmailCode(email.trim());
      setStep("code");
    } catch (err) {
      setProblem(
        err instanceof RateLimited
          ? t("auth.tooMany")
          : err instanceof Offline
            ? t("auth.unreachable")
            : t("auth.unreachable"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setProblem(null);
    try {
      onSignedIn(await auth.verifyEmailCode(email.trim(), code));
    } catch (err) {
      setProblem(err instanceof Offline ? t("auth.unreachable") : t("auth.badCode"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <div className="signin-card">
        <div className="side-brand" style={{ padding: 0 }}>
          <span className="mark" aria-hidden="true" />
          <span className="side-word">{t("shell.title")}</span>
        </div>
        <div>
          <h1 className="title">{t("auth.title")}</h1>
          <p className="lead">{t("auth.body")}</p>
        </div>

        {(error || problem) && (
          <p className="error" role="alert">
            {problem ?? error}
          </p>
        )}

        {step === "choose" && (
          <>
            {providers?.has("google") && (
              <button type="button" className="provider" onClick={() => onProvider("google")}>
                <span className="provider-mark">G</span>
                <span>{t("auth.google")}</span>
                <span aria-hidden="true" />
              </button>
            )}
            {providers?.has("azure") && (
              <button type="button" className="provider" onClick={() => onProvider("azure")}>
                <span className="provider-mark">⊞</span>
                <span>{t("auth.microsoft")}</span>
                <span aria-hidden="true" />
              </button>
            )}
            {providers && providers.size > 0 && (
              <p className="or">
                <span>or</span>
              </p>
            )}
            <button type="button" className="btn btn-primary" onClick={() => setStep("email")}>
              {t("auth.email")}
            </button>
          </>
        )}

        {step === "email" && (
          <form
            className="field"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <label className="field-label" htmlFor="admin-email">
              {t("auth.emailLabel")}
            </label>
            <input
              id="admin-email"
              className="input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t("shell.loading") : t("auth.send")}
            </button>
          </form>
        )}

        {step === "code" && (
          <form
            className="field"
            onSubmit={(e) => {
              e.preventDefault();
              void verify();
            }}
          >
            <label className="field-label" htmlFor="admin-code">
              {t("auth.codeLabel")}
            </label>
            <input
              id="admin-code"
              className="input mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
              {busy ? t("shell.loading") : t("auth.verify")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setStep("email")}>
              {t("auth.changeEmail")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
